/**
 * Credential entity type: the service account (public API principal). Split
 * out of entityTypeRegistry.ts — see that file for the merge point.
 *
 * Its shape: the credential sub-header (kind · reach · scope), a "Data
 * access" table instead of the generic permissions badges, an Access section
 * carrying the credential's own actions, and NO generic Files section or
 * header delete — the former is meaningless for a principal and the latter
 * would orphan the credential's secrets (delete lives in the Access section,
 * where it sweeps them first). The permissions grant is write-once at
 * creation, so it renders read-only with no editor.
 */

import { getDisplayName } from '../../utils/getDisplayName';
import { CredentialAccessRows } from '../credentials/credential-access-rows';
import { CredentialActionsSection } from '../credentials/credential-actions-section';
import { CredentialSubHeader } from '../sections/credential-sub-header';
import type { EntityTypeConfig } from './entityTypeRegistry';

/** Credential entity types: service accounts. */
export const CREDENTIAL_ENTITY_TYPES: Record<string, EntityTypeConfig> = {
  ahoy_service_account: {
    getDisplayName,
    showAvatar: false,
    // Reach is stated once, in the sub-header, as a badge. The created/updated
    // timestamps are hidden too — the meta line below the overview shows the
    // creation date; the timestamp rows would duplicate it in "View all values".
    hiddenProperties: ['ahoy_reach', 'ahoy_created_at', 'ahoy_updated_at'],
    // Both "Created by" edges are hidden from the field list (view + edit) — the
    // read-only meta line below the overview renders the creator once. The legacy
    // ahoy_service_account_to_creator edge is security-load-bearing and immutable
    // (value_removal_access: none); this is display-only. See issue #14027.
    hiddenAssociations: [
      'ahoy_service_account_to_creator',
      'ahoy_entity_to_creator',
    ],
    recordMeta: {
      variant: 'inline',
      // Prefer the core provenance edge; fall back to the legacy service-account
      // edge for records minted before the core cascade existed.
      creatorAssociationTypes: [
        'ahoy_entity_to_creator',
        'ahoy_service_account_to_creator',
      ],
    },
    // A principal has no file association; the wildcard "created by" edge
    // must not conjure one (see useFileAssociation).
    hideFilesSection: true,
    // The header's generic delete would leave the credential's secrets
    // behind; the Access section's delete sweeps them first.
    showRecordActionMenu: false,
    customPropertyReadRenderers: {
      ahoy_permissions: CredentialAccessRows,
    },
    components: {
      subHeader: CredentialSubHeader,
      typeSpecificSections: [
        {
          kind: 'custom',
          key: 'credential-access',
          component: CredentialActionsSection,
        },
      ],
    },
    displayName: 'Service account',
    pluralDisplayName: 'Service accounts',
    color: 'gray',
    bannerGradient: 'linear-gradient(135deg, #475569 0%, #94A3B8 100%)',
  },
};
